'use client';

import { useLayoutEffect, useMemo, useRef, useState, type FC, type MouseEvent } from 'react';
import { List } from 'lucide-react';
import { useReducedMotion } from 'motion/react';

import type { THeading } from '@/components/rich-text-renderer/types';
import { cn } from '@/utils/cn';

import { useActiveHeading } from './use-active-heading';

/**
 * The article's table of contents (2026-09-14), drawn as the lot page draws
 * its sale history: the same hairline rail, the same dots, the same label
 * row — so the two detail pages read as one. It reads as PROGRESS: the rail
 * is gold from the top down to the section in view, the sections already
 * read carry filled gold dots and brighter titles, the one in view glows,
 * and the rest are hollow and grey on a grey rail (2026-09-14). A click
 * scrolls to the heading (its `scroll-mt-28` keeps it under the header) and
 * writes the hash without a jump. Rendered twice by the page, pinned in the
 * left column from `lg` and folded above the article below it — with its
 * own heading ids, suffixed `-nav` so a section titled "Spis treści" cannot
 * collide.
 */

const DOT = {
  done: 'border-action/80 bg-action/80',
  next: 'border-action bg-action shadow-[0_0_12px_rgba(251,192,11,0.6)]',
  muted: 'border-neutral-700 bg-background',
} as const;

/**
 * The grey rail is drawn per item (`before`, through the item's whole box;
 * items are spaced with padding, not gap, so the segments touch; the first
 * starts at its dot, the last ends at its dot). The gold progress is ONE
 * overlay on the list, its height measured to the active dot and animated —
 * segments that switch on and off cannot ease, and the line has to travel
 * (2026-09-14).
 */

/** Where the first dot's centre sits below the list's top: `top-[3px]` on the dot plus half of 11px. */
const FIRST_DOT_PX = 8;

type TArticleToc = {
  headings: THeading[];
  headingId?: string;
  className?: string;
  /** Folded behind its label — for the narrow layout, where it sits above the article. */
  collapsible?: boolean;
};

export const ArticleToc: FC<TArticleToc> = ({ headings, headingId = 'spis-tresci-nav', className, collapsible = false }) => {
  const ids = useMemo(() => headings.map(heading => heading.id), [headings]);
  const active = useActiveHeading(ids);
  const prefersReducedMotion = useReducedMotion();
  const listRef = useRef<HTMLOListElement>(null);
  const [progress, setProgress] = useState(0);

  // The line runs from the first dot's centre to the active dot's centre,
  // measured from the dots' own rectangles (no dependence on offset parents
  // or child order). Measured again when the fonts land, since Oswald
  // reflows the titles, and on resize.
  useLayoutEffect(() => {
    const measure = () => {
      const dots = listRef.current?.querySelectorAll<HTMLElement>('[data-dot]');
      const first = dots?.[0];
      const current = dots?.[active];

      if (!first || !current) {
        setProgress(0);

        return;
      }

      const centre = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect();

        return rect.top + rect.height / 2;
      };

      setProgress(Math.max(0, Math.round(centre(current) - centre(first))));
    };

    measure();
    window.addEventListener('resize', measure);
    document.fonts?.ready.then(measure).catch(() => undefined);

    return () => window.removeEventListener('resize', measure);
  }, [active, headings.length]);

  if (headings.length < 2) return null;

  const jump = (id: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(id);

    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    window.history.replaceState(null, '', `#${id}`);
  };

  const label = (
    <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500">
      <List size={13} className="text-action/70" aria-hidden />
      Spis treści · {headings.length}
    </span>
  );

  const rail = (
    <ol ref={listRef} className="relative flex flex-col pl-6">
      <span
        aria-hidden
        className="pointer-events-none absolute -left-[1.5px] z-10 w-0.5 rounded-full bg-action transition-[height] duration-500 ease-out motion-reduce:transition-none"
        style={{ top: FIRST_DOT_PX, height: progress }}
      />
      {headings.map((heading, index) => {
        const tone = index < active ? 'done' : index === active ? 'next' : 'muted';

        return (
          <li
            key={heading.id}
            className={cn(
              'relative pb-4 last:pb-0',
              // 2px lines, centred under the 11px dots (dot centre = 30px − 5.5px out); 1px in neutral-800 vanished on this background.
              'before:absolute before:-left-[25.5px] before:top-0 before:bottom-0 before:w-0.5 before:bg-neutral-700 first:before:top-2 last:before:bottom-auto last:before:h-2'
            )}
          >
            <span
              aria-hidden
              data-dot
              className={cn(
                'absolute -left-[30px] top-[3px] z-20 h-[11px] w-[11px] rounded-full border-2 transition-all duration-300',
                DOT[tone],
                heading.level === 3 && '-left-[28px] top-[5px] h-[7px] w-[7px]'
              )}
            />
            <a
              href={`#${heading.id}`}
              onClick={jump(heading.id)}
              aria-current={tone === 'next' ? 'true' : undefined}
              className={cn(
                'block leading-tight transition-colors duration-300 hover:text-action focus:outline-none focus-visible:text-action focus-visible:underline focus-visible:underline-offset-4',
                heading.level === 2 ? 'font-oswald text-lg' : 'pl-3 text-sm',
                tone === 'next' ? 'text-foreground' : tone === 'done' ? 'text-neutral-300' : 'text-neutral-500'
              )}
            >
              {heading.text}
            </a>
          </li>
        );
      })}
    </ol>
  );

  return (
    <nav aria-labelledby={headingId} className={cn('mt-8', className)}>
      <h2 id={headingId} className="sr-only">
        Spis treści
      </h2>
      {collapsible ? (
        <details className="rounded-lg border border-neutral-800 bg-backgroundLight/40 p-4">
          <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">{label}</summary>
          <div className="mt-5">{rail}</div>
        </details>
      ) : (
        <>
          {label}
          <div className="mt-4">{rail}</div>
        </>
      )}
    </nav>
  );
};
